/**
 * サーバとやりとりする型。server/src/types.ts の写しなので、片方を直したら
 * もう片方も直す必要がある。ワークスペースを跨いだ型の共有より、
 * 独立してビルドできることを優先している。
 */

export type Source = 'youtube' | 'spotify' | 'apple' | 'search';
export type RoundBasis = 'pending' | 'lifetime';

export type Member = {
  id: string;
  name: string;
  color: string;
  joinedAt: number;
  online: boolean;
  audio: boolean;
  lifetimeAdds: number;
};

export type Track = {
  id: string;
  memberId: string;
  round: number;
  seq: number;
  videoId: string;
  title: string;
  artist: string | null;
  durationMs: number;
  thumbnail: string | null;
  source: Source;
  originalUrl: string | null;
  addedAt: number;
};

export type NowPlaying = {
  track: Track;
  startedAtMs: number;
  positionMs: number;
  paused: boolean;
};

export type RoomState = {
  code: string;
  hostId: string | null;
  leaderId: string | null;
  members: Member[];
  queue: Track[];
  nowPlaying: NowPlaying | null;
  history: Track[];
  roundBasis: RoundBasis;
  serverTimeMs: number;
};

export type Candidate = {
  videoId: string;
  title: string;
  artist: string | null;
  durationMs: number;
  thumbnail: string | null;
  source: Source;
  originalUrl: string | null;
};

export type ClientMessage =
  | { t: 'hello'; roomCode: string; token?: string; name?: string }
  | { t: 'ping'; t0: number }
  | { t: 'add'; input: string }
  | { t: 'search'; query: string }
  | { t: 'addCandidate'; candidate: Candidate }
  | { t: 'remove'; trackId: string }
  | { t: 'skip' }
  | { t: 'pause' }
  | { t: 'resume' }
  | { t: 'seek'; positionMs: number }
  | { t: 'setAudio'; enabled: boolean }
  | { t: 'setRoundBasis'; basis: RoundBasis }
  | { t: 'claimLeader' }
  | {
      t: 'reportMeta';
      trackId: string;
      durationMs: number;
      title?: string;
      artist?: string;
    }
  | { t: 'ended'; trackId: string }
  | { t: 'syncPosition'; trackId: string; positionMs: number }
  | { t: 'leave' };

export type ServerMessage =
  | { t: 'welcome'; member: Member; token: string; state: RoomState }
  | { t: 'state'; state: RoomState }
  | { t: 'pong'; t0: number; tServer: number }
  | { t: 'searchResults'; query: string; results: Candidate[] }
  | { t: 'notice'; level: 'info' | 'error'; message: string };

export function formatDuration(ms: number): string {
  if (!ms || ms <= 0) return '--:--';
  const total = Math.round(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  return h > 0
    ? `${h}:${mm}:${String(s).padStart(2, '0')}`
    : `${mm}:${String(s).padStart(2, '0')}`;
}

export const SOURCE_LABEL: Record<Source, string> = {
  youtube: 'YouTube',
  spotify: 'Spotify',
  apple: 'Apple Music',
  search: '検索',
};
